import produce from 'immer';
import create from 'zustand';

import { ImmerUpdateFn } from '@utils/types';

type ExtraStateStoreState = {
  addedActionIds: string[];
  customNames: Record<string, string>;
};

export const createExtraStateStore = (initialState?: ExtraStateStoreState) => {
  const useStore = create<ExtraStateStoreState>(() => ({
    addedActionIds: initialState?.addedActionIds ?? [],
    customNames: initialState?.customNames ?? {},
  }));

  const update = (fn: ImmerUpdateFn<ExtraStateStoreState>) => {
    return useStore.setState((state) => produce(state, fn));
  };

  const addActionId = (id: string, onFieldsNeeded?: (newFieldIds: string[]) => void) => {
    const isNew = !useStore.getState().addedActionIds.includes(id);

    update((state) => {
      if (!state.addedActionIds.includes(id)) {
        state.addedActionIds.push(id);
      }
    });

    // Notify if this was a new field and callback provided
    if (isNew && onFieldsNeeded) {
      onFieldsNeeded([id]);
    }
  };

  const removeActionId = (id: string) => {
    update((state) => {
      state.addedActionIds = state.addedActionIds.filter((actionId) => actionId !== id);
    });
  };

  const setCustomName = (id: string, name: string) => {
    update((state) => {
      state.customNames[id] = name;
    });
  };

  const removeCustomName = (id: string) => {
    update((state) => {
      const newCustomNames = { ...state.customNames };
      delete newCustomNames[id];
      state.customNames = newCustomNames;
    });
  };

  const sync = (extraState: ExtraStateStoreState) => {
    update(() => {
      return extraState;
    });
  };

  const useAddedActionIds = () => useStore((state) => state.addedActionIds);
  const useCustomNames = () => useStore((state) => state.customNames);

  return {
    useAddedActionIds,
    useCustomNames,
    actions: {
      getState: useStore.getState,
      sync,
      addActionId,
      removeActionId,
      setCustomName,
      removeCustomName,
    },
  };
};

export type ExtraStateStore = ReturnType<typeof createExtraStateStore>;
